/// <amd-module name="Pipeline/Analytics/PipelineDurationCommon/Sources/PipelineDurationCommonSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { IReportContext, TrendBy } from "Analytics/Shared/Common/Types";
import { getDateFilter, parseReturnDateIntoDisplayForm } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { ITrendChartAggregateData, ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { percentile_value } from "Pipeline/Analytics/Common/Constants";
import { PipelineReportSource } from "Pipeline/Analytics/Common/Sources/PipelineReportSource";
import { BuildOutcome } from "Pipeline/Analytics/Common/Types";
import * as Resources from "Pipeline/Analytics/PipelineDurationCommon/Resources";
import { SuccessfulPipelines } from "Pipeline/Analytics/Common/Constants";
import { IMaxDurationTaskData, IPipelineDurationData } from "Pipeline/Analytics/PipelineDurationCommon/Types";

export abstract class PipelineDurationCommonSource extends PipelineReportSource {
    public async queryPipelineDurationTrendData(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<ITrendChartData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;

        const filterStr = `filter(${this._getPipelineContextFilter(reportContext)} ${this._getBranchFilter(
            confValues.branchItems
        )} and ${getDateFilter(confValues.trendBy, confValues.period)} and ${this._getSuccessfulPipelineFilter()})`;
        const computeStr = `compute(${this._getDurationPercentileWithGroupByForCompute(
            percentile_value,
            this._getTotalDurationSeconds(),
            this._getGroupByCompletedDate()
        )})`;
        const groupBy = `groupby((${this._getDurationPercentile()}, ${this._getGroupByCompletedDate()}),aggregate(${this._getTotalCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${computeStr}/${groupBy}`;
        queryOptions.$orderby = ` ${this._getGroupByCompletedDate()} asc `;

        const successfulPipelineDurationPerDay = await this._queryPipelineDurationTrendData(queryOptions);
        const chartData: ITrendChartData = { confValues: confValues, primaryChartData: {}, secondaryChartData: [] };
        chartData.primaryChartData[SuccessfulPipelines] = successfulPipelineDurationPerDay;
        return chartData;
    }

    public queryPipelineDurationData(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<IPipelineDurationData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;
        const filterStr = `filter(${this._getPipelineContextFilter(reportContext)} ${this._getBranchFilter(
            confValues.branchItems
        )} and ${getDateFilter(confValues.trendBy, confValues.period)} and ${this._getSuccessfulPipelineFilter()})`;
        const computeStr = `compute(${this._getDurationPercentileForCompute(percentile_value)})`;
        const groupBy = `groupby((${this._getDurationPercentile()}), aggregate(${this._getTotalCountForAggregation()}, ${this._getTotalDurationForAggregation()}))`;

        queryOptions.$apply = `${filterStr}/${computeStr}/${groupBy}`;
        return this._queryPipelineDuration(queryOptions);
    }

    public getMaxDurationTaskData(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<IMaxDurationTaskData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const filterStr: string = this._getMaxDurationTaskQueryFilter(reportContext, confValues);
        const groupByTaskStr = `groupby((${this._getGroupByTask()}), aggregate(${this._getActivityTotalDurationForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByTaskStr}`;
        queryOptions.$orderby = ` TotalDuration desc `;

        return this._getMaxDurationTask(queryOptions);
    }

    protected getDurationBuildOutcomeCondition(): string {
        return `(${PipelineEntities.PipelineRunOutcome} eq '${BuildOutcome.Succeed}' or ${PipelineEntities.PipelineRunOutcome} eq '${BuildOutcome.PartiallySucceeded}') and 
        (CanceledCount ne 1 and SkippedCount ne 1 and AbandonedCount ne 1)`;
    }

    private async _queryPipelineDurationTrendData(queryOptions: ODataQueryOptions): Promise<ITrendChartAggregateData[]> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }
        const chartData: ITrendChartAggregateData[] = [];
        // Iterate over all data
        (data.value as any[]).forEach(d => {
            const date = parseReturnDateIntoDisplayForm(TrendBy.Days, d);
            const durationPercentile = this._parseDurationPercentile(d);
            const totalCount = this._parseTotalCount(d);
            chartData.push({
                date: date,
                aggrValue: durationPercentile,
                customData: {
                    label: Resources.TotalPipelines,
                    value: totalCount
                }
            });
        });

        return chartData;
    }

    private async _queryPipelineDuration(queryOptions: ODataQueryOptions): Promise<IPipelineDurationData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("data is empty.");
        }

        const dataValues = data.value as IPipelineDurationData[];
        const pipelineDurationData: IPipelineDurationData = { pipelineCount: 0, totalDuration: 0, percentileDuration: 0 };

        if (dataValues && dataValues.length > 0) {
            const dataValue = dataValues[0];
            pipelineDurationData.pipelineCount = this._parseTotalCount(dataValue);
            pipelineDurationData.totalDuration = this._parseTotalDuration(dataValue);
            pipelineDurationData.percentileDuration = this._parseDurationPercentile(dataValue);
        }

        return pipelineDurationData;
    }

    private async _getMaxDurationTask(queryOptions: ODataQueryOptions): Promise<IMaxDurationTaskData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("data is empty.");
        }

        const dataValues = data.value as IMaxDurationTaskData[];
        let allTasksDuration = 0;
        dataValues.forEach(d => {
            allTasksDuration += this._parseTotalDuration(d);
        });

        const maxDurationTaskData: IMaxDurationTaskData = { taskName: "", taskDuration: 0, allTasksDuration: allTasksDuration };
        if (dataValues && dataValues.length > 0) {
            const topDataValue = dataValues[0];
            maxDurationTaskData.taskName = this._parseTaskDisplayName(topDataValue);
            maxDurationTaskData.taskDuration = this._parseTotalDuration(topDataValue);
        }

        return maxDurationTaskData;
    }
}
